/**
 * useFileUpload — shared upload logic for all file-attachment surfaces.
 *
 * Provides:
 *  - 100 MB video / 25 MB non-video size gate
 *  - Client-side video compression (canvas + MediaRecorder, 1080p @ 2 Mbps)
 *    only for videos > 30 MB (already-small videos upload immediately)
 *  - 90-second compression failsafe timeout
 *  - Axios-based upload with 10-minute timeout for videos
 *  - isCompressing / compressionProgress / isUploading / uploadProgress state
 *
 * Usage:
 *   const { uploadFile, isCompressing, compressionProgress, isUploading } =
 *     useFileUpload({ horseId: horse.id, category: 'note_file', onSuccess });
 */

import { useState, useCallback } from 'react';
import { toast } from 'sonner';
import api from '../services/api';

const VIDEO_MAX_MB       = 100;
const NON_VIDEO_MAX_MB   = 25;
const COMPRESS_THRESHOLD = 30 * 1024 * 1024;  // only compress if > 30 MB

export function useFileUpload({ category = 'file', horseId, onSuccess } = {}) {
  const [isCompressing,       setIsCompressing]       = useState(false);
  const [compressionProgress, setCompressionProgress] = useState(0);
  const [isUploading,         setIsUploading]         = useState(false);
  const [uploadProgress,      setUploadProgress]      = useState(0);

  // ── Client-side video compression ─────────────────────────────────────────
  const compressVideo = useCallback((file) => new Promise((resolve) => {
    if (file.size <= COMPRESS_THRESHOLD) { resolve(file); return; }

    const video     = document.createElement('video');
    const objectUrl = URL.createObjectURL(file);
    video.src        = objectUrl;
    video.muted      = true;
    video.playsInline = true;

    let animFrame;
    let recorder;

    const failsafe = setTimeout(() => {
      cancelAnimationFrame(animFrame);
      try { if (recorder && recorder.state !== 'inactive') recorder.stop(); } catch {}
      URL.revokeObjectURL(objectUrl);
      resolve(file);
    }, 90_000);

    video.onloadedmetadata = () => {
      const MAX_HEIGHT = 1080;
      const scale  = Math.min(1, MAX_HEIGHT / video.videoHeight);
      const width  = Math.round(video.videoWidth  * scale);
      const height = Math.round(video.videoHeight * scale);

      const canvas = document.createElement('canvas');
      canvas.width  = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');

      const mimeType = ['video/mp4', 'video/webm;codecs=h264', 'video/webm']
        .find(t => MediaRecorder.isTypeSupported(t)) || '';

      try {
        recorder = new MediaRecorder(canvas.captureStream(30), {
          mimeType,
          videoBitsPerSecond: 2_000_000,
        });
      } catch {
        clearTimeout(failsafe);
        URL.revokeObjectURL(objectUrl);
        resolve(file);
        return;
      }

      const chunks = [];
      recorder.ondataavailable = e => { if (e.data.size > 0) chunks.push(e.data); };
      recorder.onstop = () => {
        clearTimeout(failsafe);
        URL.revokeObjectURL(objectUrl);
        const blob    = new Blob(chunks, { type: mimeType || file.type });
        const outFile = blob.size > 0 && blob.size < file.size
          ? new File([blob], file.name.replace(/\.[^.]+$/, '.mp4'), { type: blob.type })
          : file;
        resolve(outFile);
      };

      const drawFrame = () => {
        ctx.drawImage(video, 0, 0, width, height);
        if (!video.paused && !video.ended) {
          animFrame = requestAnimationFrame(drawFrame);
          setCompressionProgress(
            video.duration > 0
              ? Math.round((video.currentTime / video.duration) * 100)
              : 0
          );
        }
      };

      video.onplay   = () => { drawFrame(); };
      video.onended  = () => {
        clearTimeout(failsafe);
        cancelAnimationFrame(animFrame);
        setCompressionProgress(100);
        recorder.stop();
      };

      recorder.start(500);
      video.play().catch(() => {
        clearTimeout(failsafe);
        cancelAnimationFrame(animFrame);
        try { if (recorder && recorder.state !== 'inactive') recorder.stop(); } catch {}
        URL.revokeObjectURL(objectUrl);
        resolve(file);
      });
    };

    video.onerror = () => {
      clearTimeout(failsafe);
      cancelAnimationFrame(animFrame);
      URL.revokeObjectURL(objectUrl);
      resolve(file);
    };
  }), []);   // setCompressionProgress is stable — no dep needed

  // ── Main upload function ───────────────────────────────────────────────────
  const uploadFile = useCallback(async (file) => {
    if (!file) return null;

    const isVideo = file.type.startsWith('video/');
    const maxMB   = isVideo ? VIDEO_MAX_MB : NON_VIDEO_MAX_MB;
    if (file.size > maxMB * 1024 * 1024) {
      toast.error(`File too large. Max size is ${maxMB}MB${isVideo ? ' for videos' : ''}.`);
      return null;
    }

    // Compress only large videos
    let fileToUpload    = file;
    const needsCompress = isVideo && file.size > COMPRESS_THRESHOLD;
    if (needsCompress) {
      setIsCompressing(true);
      setCompressionProgress(0);
      try {
        fileToUpload = await compressVideo(file);
      } catch {
        fileToUpload = file;
      } finally {
        setIsCompressing(false);
        setCompressionProgress(0);
      }
    }

    setIsUploading(true);
    setUploadProgress(0);
    try {
      const formData = new FormData();
      formData.append('file', fileToUpload);
      formData.append('category', category);
      if (horseId) formData.append('horse_id', horseId);

      const response = await api.post('/files/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        ...(isVideo && { timeout: 10 * 60 * 1000 }),
        onUploadProgress: (e) => {
          setUploadProgress(Math.round((e.loaded * 100) / e.total));
        },
      });

      onSuccess?.(response.data);
      return response.data;
    } catch {
      toast.error('Upload failed');
      return null;
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  }, [category, horseId, onSuccess, compressVideo]);

  return {
    uploadFile,
    isCompressing,
    compressionProgress,
    isUploading,
    uploadProgress,
  };
}
